import { useEffect, useState } from 'react';
import { netBase } from '../discord/embedded.js';

/**
 * RoomLostOverlay — what the table sees when the room stops existing.
 *
 * Rooms live in the server's memory, so a deploy (or a crash) takes every game
 * in flight with it. Before this existed the client said nothing: the socket
 * went quiet, the phase clock kept ticking down to 00:00 on its own, the skip
 * button stayed at 0/N, and the Ringmaster simply never spoke again — a table
 * mid-argument had no way to tell a dead server from a slow one. A real session
 * died that way (room LHUQ, killed mid-discussion by a deploy).
 *
 * So: say it plainly, then fix it without asking. Poll /api/health until the
 * server answers, then reload — which re-boots the Activity, re-POSTs the
 * Discord session and lands everyone in a fresh lobby for the same instance.
 * If health never comes back, the message stays honest and offers the reload.
 */

/** How often to ask whether the server is back. */
const POLL_MS = 2000;
/**
 * Consecutive healthy answers required before reloading. A server that is in
 * the middle of shutting down still answers /api/health for a beat — reloading
 * on that first "yes" lands on a server that is gone by the time the page boots
 * (observed: instant reload → "Something went wrong entering the Activity").
 * Two OKs, POLL_MS apart, means it is genuinely back up.
 */
const HEALTHY_STREAK = 2;
/** Give up auto-recovering after this long (the button stays). */
const GIVE_UP_MS = 120_000;

export function RoomLostOverlay() {
  const [waited, setWaited] = useState(0);
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let healthy = 0;
    const startedAt = Date.now();
    const tick = async (): Promise<void> => {
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;
      setWaited(elapsed);
      if (elapsed > GIVE_UP_MS) {
        setGaveUp(true);
        return;
      }
      try {
        const res = await fetch(`${netBase()}/api/health`, { cache: 'no-store' });
        healthy = res.ok ? healthy + 1 : 0;
        if (healthy >= HEALTHY_STREAK && !cancelled) {
          window.location.reload();
          return;
        }
      } catch {
        healthy = 0; // still down — the streak restarts
      }
      if (!cancelled) window.setTimeout(() => void tick(), POLL_MS);
    };
    // Never poll in the same tick the socket died: the dying server can still
    // answer. Start one interval late.
    window.setTimeout(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        textAlign: 'center',
        background: 'rgba(6,4,6,0.92)',
        backdropFilter: 'blur(6px)',
      }}
    >
      <div style={{ maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 26,
            color: 'var(--text-primary)',
            margin: 0,
            letterSpacing: '0.04em',
          }}
        >
          The tent came down
        </h2>
        <p style={{ fontFamily: 'var(--font-ui)', fontSize: 14, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
          {gaveUp
            ? 'The Ringmaster is still away. Try again in a moment.'
            : 'This game ended early — the show is being rebuilt. Bringing you back to a fresh lobby…'}
        </p>
        {!gaveUp && waited > 6000 && (
          <p style={{ fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--text-tertiary)', margin: 0 }}>
            still waiting… {Math.round(waited / 1000)}s
          </p>
        )}
        <button
          type="button"
          className="rm-hover-pill"
          onClick={() => window.location.reload()}
          style={{
            fontFamily: 'var(--font-ui)',
            fontWeight: 600,
            fontSize: 15,
            padding: '12px 26px',
            borderRadius: 'var(--radius-pill)',
            border: '1px solid rgba(201,164,78,0.45)',
            background: 'rgba(126,92,68,0.28)',
            color: '#f2d9a0',
            cursor: 'pointer',
          }}
        >
          Rejoin now
        </button>
      </div>
    </div>
  );
}
